import gql from "graphql-tag";
import {useMemo} from "react";

import {useQuery} from "@khan/data-gql";
import type {GqlOperation} from "@khan/data-gql-core";

import type {
    getTeacherCourses,
    getTeacherCourses_user as getTeacherCoursesUser,
    getTeacherCoursesVariables,
} from "./__graphql-types__/getTeacherCourses.ts";
import type {
    getTeacherGradeLevels,
    getTeacherGradeLevels_user as getTeacherGradeLevelsUser,
    getTeacherGradeLevelsVariables,
} from "./__graphql-types__/getTeacherGradeLevels.ts";

const GET_TEACHER_GRADE_LEVELS: GqlOperation<
    getTeacherGradeLevels,
    getTeacherGradeLevelsVariables
> = gql`
    query getTeacherGradeLevels($kaid: String!) {
        user(kaid: $kaid) {
            id
            teacherGradeLevels
        }
    }
`;

const GET_TEACHER_COURSES: GqlOperation<
    getTeacherCourses,
    getTeacherCoursesVariables
> = gql`
    query getTeacherCourses($kaid: String!) {
        user(kaid: $kaid) {
            id
            teacherCourses
        }
    }
`;

export type TeacherGradeLevelResult =
    | {status: "loading"}
    | {status: "error"; error: Error}
    | {status: "aborted"}
    | {status: "no-data"}
    | {
          status: "success";
          gradeLevels: NonNullable<
              getTeacherGradeLevelsUser["teacherGradeLevels"]
          >;
      };

export type TeacherCoursesResult =
    | {status: "loading"}
    | {status: "error"; error: Error}
    | {status: "aborted"}
    | {status: "no-data"}
    | {
          status: "success";
          courses: NonNullable<getTeacherCoursesUser["teacherCourses"]>;
      };

export const useTeacherGradeLevels = (
    kaid: string,
): TeacherGradeLevelResult => {
    const [result] = useQuery(GET_TEACHER_GRADE_LEVELS, {variables: {kaid}});

    return useMemo<TeacherGradeLevelResult>(() => {
        switch (result.status) {
            case "success": {
                const teacherGradeLevels = result.data.user?.teacherGradeLevels;
                return teacherGradeLevels
                    ? {status: "success", gradeLevels: teacherGradeLevels}
                    : {status: "no-data"};
            }
            case "loading":
                return {status: "loading"};
            case "error":
                return {status: "error", error: result.error};
            case "aborted":
                return {status: "aborted"};
            default:
                return {status: "no-data"};
        }
    }, [result]);
};

export const useTeacherCourses = (kaid: string): TeacherCoursesResult => {
    const [result] = useQuery(GET_TEACHER_COURSES, {variables: {kaid}});

    return useMemo<TeacherCoursesResult>(() => {
        switch (result.status) {
            case "success": {
                const teacherCourses = result.data.user?.teacherCourses;
                return teacherCourses
                    ? {status: "success", courses: teacherCourses}
                    : {status: "no-data"};
            }
            case "loading":
                return {status: "loading"};
            case "error":
                return {status: "error", error: result.error};
            case "aborted":
                return {status: "aborted"};
            default:
                return {status: "no-data"};
        }
    }, [result]);
};
